import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  container: {
    marginBottom: 30,
  },
  phaseHeader: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 8,
    color: '#333333',
  },
  phaseDescription: {
    fontSize: 8,
    marginBottom: 15,
    lineHeight: 1.4,
    color: '#666666',
  },
  table: {
    width: '100%',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f5f5f5',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    paddingVertical: 8,
    paddingHorizontal: 6,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    minHeight: 30,
  },
  headerCellAbility: {
    fontSize: 8,
    fontWeight: 600,
    color: '#444444',
    backgroundColor: '#F8F9FA',
    padding: 6,
  },
  headerCellSkills: {
    fontSize: 8,
    fontWeight: 600,
    color: '#444444',
    backgroundColor: '#E8F5E9',
    padding: 6,
  },
  headerCellKnowledge: {
    fontSize: 8,
    fontWeight: 600,
    color: '#444444',
    backgroundColor: '#E3F2FD',
    padding: 6,
  },
  headerCellMindset: {
    fontSize: 8,
    fontWeight: 600,
    color: '#444444',
    backgroundColor: '#FFF3E0',
    padding: 6,
  },
  headerCellInstruments: {
    fontSize: 8,
    fontWeight: 600,
    color: '#444444',
    backgroundColor: '#F3E5F5',
    padding: 6,
  },
  cell: {
    fontSize: 7,
    color: '#333333',
  },
  abilityColumn: {
    width: '25%',
    backgroundColor: '#F8F9FA',
  },
  skillsColumn: {
    width: '18.75%',
    backgroundColor: '#E8F5E9',
  },
  knowledgeColumn: {
    width: '18.75%',
    backgroundColor: '#E3F2FD',
  },
  mindsetColumn: {
    width: '18.75%',
    backgroundColor: '#FFF3E0',
  },
  instrumentsColumn: {
    width: '18.75%',
    backgroundColor: '#F3E5F5',
  },
  columnContent: {
    padding: 4,
    paddingTop: 6,
    paddingBottom: 6,
  },
  competencyItem: {
    marginBottom: 4,
    paddingBottom: 4,
    padding: 2,
    borderBottomWidth: 0.5,
    borderBottomColor: '#e0e0e0',
    borderBottomStyle: 'dashed',
    paddingHorizontal: 6,
  },
  firstCompetencyItem: {
    paddingTop: 8,
  },
  lastCompetencyItem: {
    marginBottom: 0,
    paddingBottom: 0,
    borderBottomWidth: 0,
  },
  abilityCell: {
    fontSize: 7,
    color: '#333333',
    fontWeight: 500,
  }
});

const PhaseGrid = ({ phase, description, data }) => {
  // Transform the data into the format needed for rendering
  const gridData = Object.entries(data).map(([ability, categories]) => ({
    ability,
    skills: categories.skills || [],
    knowledge: categories.knowledge || [],
    mindset: categories.mindset || [],
    instruments: categories.tool || [] // Note: 'tool' in API maps to 'instruments' in display
  }));

  return (
    <View style={styles.container}>
      <Text style={styles.phaseHeader}>{phase}</Text>
      <Text style={styles.phaseDescription}>{description}</Text>

      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={[styles.headerCellAbility, styles.abilityColumn]}>Ability</Text>
          <Text style={[styles.headerCellSkills, styles.skillsColumn]}>Skills</Text>
          <Text style={[styles.headerCellKnowledge, styles.knowledgeColumn]}>Knowledge</Text>
          <Text style={[styles.headerCellMindset, styles.mindsetColumn]}>Mindset</Text>
          <Text style={[styles.headerCellInstruments, styles.instrumentsColumn]}>Instruments</Text>
        </View>

        {gridData.length > 0 ? (
          gridData.map((row, index) => (
            <View key={index} style={styles.tableRow}>
              <View style={styles.abilityColumn}>
                <Text style={[styles.abilityCell, styles.columnContent]}>{row.ability}</Text>
              </View>
              
              <View style={styles.skillsColumn}>
                {row.skills.map((skill, i) => (
                  <View 
                    key={i} 
                    style={[
                      styles.competencyItem,
                      i === 0 && styles.firstCompetencyItem,
                      i === row.skills.length - 1 && styles.lastCompetencyItem
                    ]}
                  >
                    <Text style={styles.cell}>{skill}</Text>
                  </View>
                ))}
              </View>

              <View style={styles.knowledgeColumn}>
                {row.knowledge.map((item, i) => (
                  <View 
                    key={i} 
                    style={[
                      styles.competencyItem,
                      i === 0 && styles.firstCompetencyItem,
                      i === row.knowledge.length - 1 && styles.lastCompetencyItem
                    ]}
                  >
                    <Text style={styles.cell}>{item}</Text>
                  </View>
                ))}
              </View>

              <View style={styles.mindsetColumn}>
                {row.mindset.map((item, i) => (
                  <View 
                    key={i} 
                    style={[
                      styles.competencyItem,
                      i === 0 && styles.firstCompetencyItem,
                      i === row.mindset.length - 1 && styles.lastCompetencyItem
                    ]}
                  >
                    <Text style={styles.cell}>{item}</Text>
                  </View>
                ))}
              </View>

              <View style={styles.instrumentsColumn}>
                {row.instruments.map((item, i) => (
                  <View 
                    key={i} 
                    style={[
                      styles.competencyItem,
                      i === 0 && styles.firstCompetencyItem,
                      i === row.instruments.length - 1 && styles.lastCompetencyItem
                    ]}
                  >
                    <Text style={styles.cell}>{item}</Text>
                  </View>
                ))}
              </View>
            </View>
          ))
        ) : (
          <View style={styles.tableRow}>
            <View style={[styles.abilityColumn, { flex: 1 }]}>
              <Text style={[styles.cell, styles.columnContent, { textAlign: 'center' }]}>
                No competencies selected for this phase
              </Text>
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

export default PhaseGrid;